import { useTranslation } from "react-i18next";

import {
  getFocusedItemId,
  HaNContext,
  type HaNFloorSuggestion,
  HaNViewStoreContext,
  selectSelection,
  useHaNView,
  useRoomsModel,
} from "@lib/logic";
import { getShouldShowHereAndNowConnectYourCalendarFullScreen, type MeetingRoom, useAppSelector } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { EmptyListPlaceholder } from "@molecules";

import { useFitNodeInView } from "../map/useFitNodeInView";
import { FloorSuggestionTile } from "../shared/FloorSuggestionTile";
import { HaNCalendarConsent } from "../shared/HaNCalendarConsent";
import { HaNCellSkeleton } from "../shared/HaNCellSkeleton";
import { HaNSlider } from "../shared/HaNSlider/HaNSlider";
import { useOpenResult } from "../shared/useOpenResult";
import { useOpenSuggestedResult } from "../shared/useOpenSuggestedResult";
import { RoomCell } from "./RoomCell";

export const RoomsCarousel = () => {
  const { tab } = useRequiredContext(HaNContext);

  if (tab !== "rooms") return null;

  return <RoomsCarouselContent />;
};

// The same rooms as the list, then each other floor's tile and its suggestions. It centres on the selected room, or
// the first one.
const RoomsCarouselContent = () => {
  const { t } = useTranslation();
  const isAskingForCalendar = useAppSelector(getShouldShowHereAndNowConnectYourCalendarFullScreen);
  const { status, rooms, suggestions } = useRoomsModel();
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const selectedId = useHaNView(state => selectSelection("rooms")(state)?.id ?? null);
  const openResult = useOpenResult();
  const openSuggestedResult = useOpenSuggestedResult();
  const fitNodeInView = useFitNodeInView();

  if (status === "loading") {
    return (
      <HaNSlider.Card className="items-center">
        <HaNCellSkeleton />
      </HaNSlider.Card>
    );
  }

  if (isAskingForCalendar) {
    return (
      <HaNSlider.Card className="w-[335px] rounded-xl p-6">
        <HaNCalendarConsent displayIllustration={false} />
      </HaNSlider.Card>
    );
  }

  if (rooms.length === 0 && !suggestions.above && !suggestions.below) {
    return (
      <HaNSlider.Card>
        <EmptyListPlaceholder text={t("hereAndNow:NoRoomsAvailable")} />
      </HaNSlider.Card>
    );
  }

  const roomIds = rooms.map(room => room.id);
  const focusedIndex = roomIds.indexOf(getFocusedItemId(roomIds, selectedId) ?? "");

  // Swiping to one of the floor's rooms selects it, like tapping it on the map. Other floors aren't on the map.
  const focusSlide = (index: number) => {
    const room = rooms[index];

    if (!room) return;

    viewStore.select("rooms", { kind: "room", id: room.id });

    fitNodeInView(room.id);
  };

  // Swiper only finds slides among its children and fragments, so these are elements rather than a component
  const renderSuggestionSlides = (suggestion: HaNFloorSuggestion<MeetingRoom> | null, direction: "above" | "below") => {
    if (!suggestion) return null;

    return (
      <>
        <HaNSlider.Slide>
          <HaNSlider.Card>
            <FloorSuggestionTile
              floorId={suggestion.floorId}
              direction={direction}
            />
          </HaNSlider.Card>
        </HaNSlider.Slide>

        {suggestion.items.map(room => (
          <HaNSlider.Slide key={room.id}>
            <HaNSlider.CardButton onClick={() => openSuggestedResult("room", room, direction)}>
              <RoomCell room={room} />
            </HaNSlider.CardButton>
          </HaNSlider.Slide>
        ))}
      </>
    );
  };

  return (
    <HaNSlider
      focusedIndex={focusedIndex}
      onFocusChange={focusSlide}>
      {rooms.length > 0 ? (
        rooms.map(room => (
          <HaNSlider.Slide key={room.id}>
            <HaNSlider.CardButton onClick={() => openResult("room", room)}>
              <RoomCell room={room} />
            </HaNSlider.CardButton>
          </HaNSlider.Slide>
        ))
      ) : (
        <HaNSlider.Slide>
          <HaNSlider.Card>
            <EmptyListPlaceholder
              text={t("hereAndNow:NoRoomsAvailable")}
              className="justify-center p-2 [&>svg]:size-14"
            />
          </HaNSlider.Card>
        </HaNSlider.Slide>
      )}
      {renderSuggestionSlides(suggestions.above, "above")}
      {renderSuggestionSlides(suggestions.below, "below")}
    </HaNSlider>
  );
};
